import { FC } from 'react'

import { Link } from '@remix-run/react'

import { IconArrowRight, IconMapPin, IconTrain } from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import { twMerge } from '~/lib/utils'
import { Office, Transportation } from '~/models/types'

// HACK: オフィス検索結果一覧でも同じ処理をしている。共通化する。
const convertTransportationToText = (transportation: Transportation) => {
  switch (transportation) {
    case 'WALK':
      return '徒歩'
    case 'BUS':
      return 'バス'
    default:
      return ''
  }
}

const createTransportationInfo = (stations: Office['stations']) => {
  return stations
    .map((station) => {
      return `${station.name}駅${convertTransportationToText(
        station.transportation,
      )}${station.time}分`
    })
    .join('、')
}

const createOtherAccessInfo = (otherAccesses: Office['otherAccesses']) => {
  if (!otherAccesses) return ''
  return otherAccesses
    .map((otherAccess) => {
      return `（${otherAccess.accessType === 'BUS' ? 'バス' : 'その他'}）${otherAccess.description}`
    })
    .join('、')
}

const transformOffices = (office: Office) => {
  return {
    id: office.id,
    officeName: office.name,
    address: office.address,
    buildingName: office.building,
    transportationInfo: createTransportationInfo(office.stations),
    otherAccessInfo: createOtherAccessInfo(office.otherAccesses),
    href: `/prefectures/${office.areas[0]?.prefectures[0]?.slug}/offices/${office.id}`,
    imageSrc:
      (
        office.images
          .sort((a, b) => a.priority - b.priority)
          .find((image) => image.isEyeCatch) || office.images[0]
      )?.url || NO_IMAGE_URL,
    spaces: office.spaces.map((space) => ({
      id: space.id,
      types: space.type.map((t) => t.name),
      price: space.monthlyCostExcludingTax,
      priceTaxIncluded: space.monthlyCostIncludingTax,
      capacity: space.maxPeople,
    })),
  }
}

type Props = {
  office: Office
  withoutSideSpacing?: boolean
  className?: string
  onClick?: React.MouseEventHandler<HTMLAnchorElement> | undefined
}

export const OfficeInfoCard: FC<Props> = ({
  office,
  withoutSideSpacing = false,
  className,
  onClick,
}) => {
  const officeInfo = transformOffices(office)

  return (
    <Link
      className={twMerge(
        'flex flex-row py-4 hover:opacity-80',
        withoutSideSpacing && 'px-0',
        className,
      )}
      onClick={onClick}
      to={officeInfo.href}
    >
      <img
        alt={`${officeInfo.officeName}の画像`}
        className="h-20 w-[122px] shrink-0 object-cover"
        src={officeInfo.imageSrc}
      />
      <div className="flex min-w-0 flex-col gap-y-0.5 px-2">
        <p className="flex-wrap text-xs font-bold leading-[130%] text-link">
          {officeInfo.officeName}
        </p>
        <div className="flex flex-row items-center gap-x-1">
          <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[51px] bg-brand-bg">
            <IconMapPin className="h-[14px] w-[14px]" color="brand" />
          </div>
          <p className="truncate text-[12px]">{officeInfo.address}</p>
        </div>
        <div className="flex flex-row items-center gap-x-1">
          <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[51px] bg-brand-bg">
            <IconTrain className="h-[14px] w-[14px]" color="brand" />
          </div>
          <p className="truncate text-[12px]">
            {officeInfo.transportationInfo || officeInfo.otherAccessInfo
              ? `${officeInfo.transportationInfo} ${officeInfo.otherAccessInfo && `、${officeInfo.otherAccessInfo}`}`
              : '-'}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center pl-1">
        <IconArrowRight />
      </div>
    </Link>
  )
}
